import { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import SiteHeader from './components/layout/SiteHeader';
import SiteFooter from './components/layout/SiteFooter';
import WhatsAppFloat from './components/layout/WhatsAppFloat';
import PageLoader from './components/layout/PageLoader';
import { I18nProvider, useI18n } from './i18n/I18nContext';
import { STORAGE_KEYS, readStored, writeStored } from './config/site';

const loadHome = () => import('./pages/HomePage');
const loadListing = () => import('./pages/ListingPage');
const loadDetail = () => import('./pages/PropertyDetailPage');
const loadAbout = () => import('./pages/AboutPage');
const loadContact = () => import('./pages/ContactPage');
const loadPrivacy = () => import('./pages/PrivacyPage');
const loadTerms = () => import('./pages/TermsPage');
const loadProject = () => import('./pages/ProjectPage');

const HomePage = lazy(loadHome);
const ListingPage = lazy(loadListing);
const PropertyDetailPage = lazy(loadDetail);
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AboutPage = lazy(loadAbout);
const ContactPage = lazy(loadContact);
const PrivacyPage = lazy(loadPrivacy);
const TermsPage = lazy(loadTerms);
const ProjectPage = lazy(loadProject);

// Public pages are fetched once the browser is idle so navigating never waits on a chunk.
function usePrefetchPages() {
  useEffect(() => {
    const load = () => [loadHome, loadListing, loadDetail, loadAbout, loadContact, loadPrivacy, loadTerms, loadProject]
      .forEach((loader) => loader().catch(() => {}));
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(load, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = window.setTimeout(load, 2500);
    return () => window.clearTimeout(timer);
  }, []);
}

const LISTING_PATHS = ['/propiedades', '/comprar', '/alquilar'];

function getRouteKey(pathname) {
  if (pathname === '/terrenos' || pathname.startsWith('/terrenos/')) {
    return 'land';
  }
  return LISTING_PATHS.includes(pathname) ? 'listing' : pathname;
}

function useRouteScroll(routeKey, hash) {
  const previousKey = useRef(routeKey);

  useLayoutEffect(() => {
    if (previousKey.current !== routeKey && !hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    previousKey.current = routeKey;
  }, [routeKey, hash]);

  useEffect(() => {
    if (!hash) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [routeKey, hash]);
}

function AppRoutes({ language, onLanguageChange, theme, onThemeChange }) {
  const location = useLocation();
  const { t } = useI18n();
  const routeKey = getRouteKey(location.pathname);
  useRouteScroll(routeKey, location.hash);
  usePrefetchPages();

  return (
    <div className="site-shell">
      <a className="skip-link" href="#contenido">{t.skipToContent}</a>
      <SiteHeader
        language={language}
        onLanguageChange={onLanguageChange}
        theme={theme}
        onThemeChange={onThemeChange}
      />

      <div className="route" key={routeKey} id="contenido" tabIndex={-1}>
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/propiedades" element={<ListingPage operation="" />} />
            <Route path="/comprar" element={<ListingPage operation="VENTA" />} />
            <Route path="/alquilar" element={<ListingPage operation="ALQUILER" />} />
            <Route path="/terrenos" element={<ListingPage operation="VENTA" land />} />
            <Route path="/terrenos/:ciudad" element={<ListingPage operation="VENTA" land />} />
            <Route path="/propiedad/:id" element={<PropertyDetailPage />} />
            <Route path="/el-molino-del-churqui" element={<ProjectPage />} />
            <Route path="/admin" element={<AdminPage labels={t} />} />
            <Route path="/quienes-somos" element={<AboutPage />} />
            <Route path="/contacto" element={<ContactPage />} />
            <Route path="/politica-de-privacidad" element={<PrivacyPage />} />
            <Route path="/terminos-y-condiciones" element={<TermsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>

      <SiteFooter />
      <WhatsAppFloat />
    </div>
  );
}

function App() {
  const [language, setLanguage] = useState(() => readStored(STORAGE_KEYS.language) || 'es');
  const [theme, setTheme] = useState(() => readStored(STORAGE_KEYS.theme) || 'light');

  useEffect(() => {
    writeStored(STORAGE_KEYS.language, language);
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  useEffect(() => {
    writeStored(STORAGE_KEYS.theme, theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <I18nProvider language={language}>
      <AppRoutes
        language={language}
        onLanguageChange={setLanguage}
        theme={theme}
        onThemeChange={setTheme}
      />
    </I18nProvider>
  );
}

export default App;
